<template>
  <div class="cook-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <h1 class="dashboard-title">CUISINE CENTRALE</h1>
      <p class="dashboard-subtitle">Statistiques & Retours</p>
    </div>

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Card 1: Top recettes -->
      <dashboard-card
        title="🏆 Top recettes"
        description="Les plats les mieux notés par les écoles"
        :badge="topBadge"
        badge-variant="green"
        link-label="Voir le classement"
        variant="featured"
        @click="showTopRecipes = true"
      />

      <!-- Card 2: À améliorer -->
      <dashboard-card
        title="⚠️ À améliorer"
        description="Recettes avec retours négatifs"
        :badge="String(recipePerformance.toImprove.length)"
        badge-variant="orange"
        link-label="Consulter"
        @click="showToImprove = true"
      />

      <!-- Card 3: Retours des écoles -->
      <dashboard-card
        title="📢 Retours écoles"
        description="Messages adressés à la cuisine par les cantines"
        :badge="String(kitchenMessages.length)"
        link-label="Lire"
        @click="showSchoolFeedback = true"
      />

      <!-- Card 4: Planification -->
      <dashboard-card
        title="🗓️ Planification"
        description="Menus à venir sur le réseau"
        :badge="planningBadge"
        link-label="Voir"
        @click="showPlanning = true"
      />

      <!-- Card 5: Gaspillage réseau -->
      <dashboard-card
        title="♻️ Gaspillage réseau"
        description="Évolution du gaspillage sur toutes les écoles"
        :badge="wasteBadge"
        :badge-variant="wasteVariation !== null && wasteVariation > 0 ? 'orange' : 'green'"
        link-label="Analyser"
        @click="showWasteStats = true"
      />
    </div>

    <!-- Modal: Top recettes -->
    <div v-if="showTopRecipes" class="modal-overlay" @click="showTopRecipes = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showTopRecipes = false">✕</button>
        <h2 class="modal-title">🏆 Recettes les mieux notées</h2>

        <p v-if="recipeLoading" class="feedback-empty">Chargement…</p>
        <p v-else-if="recipeError" class="feedback-empty">{{ recipeError }}</p>
        <p v-else-if="!recipePerformance.top.length" class="feedback-empty">
          Aucune recette notée sur la période. Les notes des superviseurs sont rattachées aux plats nommés du menu
          importé.
        </p>
        <div v-else class="ranking-list">
          <div v-for="(recipe, idx) in recipePerformance.top" :key="recipe.name" class="ranking-card">
            <div class="ranking-number">{{ idx + 1 }}</div>
            <div class="ranking-details">
              <h4 class="ranking-name">{{ recipe.name }}</h4>
              <div class="ranking-score">
                <span class="score-emoji">😊</span>
                <span class="score-value">{{ formatRating(recipe.averageRating) }}</span>
                <span class="score-meta">
                  {{ recipe.ratingCount }} note{{ recipe.ratingCount > 1 ? "s" : "" }} ·
                  {{ recipe.canteenCount }} école{{ recipe.canteenCount > 1 ? "s" : "" }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: À améliorer -->
    <div v-if="showToImprove" class="modal-overlay" @click="showToImprove = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showToImprove = false">✕</button>
        <h2 class="modal-title">⚠️ Recettes à améliorer</h2>

        <p v-if="recipeLoading" class="feedback-empty">Chargement…</p>
        <p v-else-if="recipeError" class="feedback-empty">{{ recipeError }}</p>
        <p v-else-if="!recipePerformance.toImprove.length" class="feedback-empty">
          Aucune recette en-deçà de la moyenne sur la période.
        </p>
        <div v-else class="improve-list">
          <div v-for="(recipe, idx) in recipePerformance.toImprove" :key="recipe.name" class="improve-card">
            <div class="improve-header">
              <h4 class="improve-name">{{ idx + 1 }}. {{ recipe.name }}</h4>
              <span class="improve-score">😐 {{ formatRating(recipe.averageRating) }}</span>
            </div>

            <div class="improve-feedback">
              <span class="feedback-count">
                {{ recipe.ratingCount }} note{{ recipe.ratingCount > 1 ? "s" : "" }} sur
                {{ recipe.canteenCount }} école{{ recipe.canteenCount > 1 ? "s" : "" }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Retours des écoles -->
    <div v-if="showSchoolFeedback" class="modal-overlay" @click="showSchoolFeedback = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showSchoolFeedback = false">✕</button>
        <h2 class="modal-title">📢 Retours des écoles</h2>

        <p v-if="kitchenLoading" class="feedback-empty">Chargement…</p>
        <p v-else-if="kitchenError" class="feedback-empty">{{ kitchenError }}</p>
        <p v-else-if="kitchenMessages.length === 0" class="feedback-empty">
          Aucun message adressé à la cuisine pour le moment.
        </p>
        <div v-else class="feedback-list">
          <div v-for="message in kitchenMessages" :key="message.id" class="feedback-card feedback-neutral">
            <div class="feedback-school">
              <strong>{{ message.canteenName }}</strong>
              <span class="feedback-time">{{ formatMealDate(message.mealDate) }}</span>
            </div>
            <p class="feedback-title">{{ message.title }}</p>
            <p class="feedback-message">{{ message.body }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Planification -->
    <div v-if="showPlanning" class="modal-overlay" @click="showPlanning = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showPlanning = false">✕</button>
        <h2 class="modal-title">🗓️ Planification</h2>

        <p v-if="planningLoading" class="feedback-empty">Chargement…</p>
        <p v-else-if="planningError" class="feedback-empty">{{ planningError }}</p>
        <p v-else-if="!menuPlanning.days.length" class="feedback-empty">
          Aucun menu planifié sur les deux prochaines semaines. Les menus proviennent des fichiers importés.
        </p>
        <div v-else class="detail-section">
          <h3 class="section-title">Prochains menus</h3>

          <div v-for="day in menuPlanning.days" :key="day.date" class="planning-card">
            <div class="planning-date">{{ formatFullDate(day.date) }}</div>
            <div v-for="menu in day.menus" :key="menu.canteenId" class="planning-canteen">
              <div v-if="day.menus.length > 1" class="planning-canteen-name">{{ menu.canteenName }}</div>
              <div class="menu-list">
                <div v-for="item in menu.items" :key="item.category" class="menu-item">
                  <span class="menu-label">{{ item.categoryLabel }}</span>
                  <span class="menu-value">
                    {{ item.name }}
                    <span v-if="item.containsPork" class="menu-pork">🐖 porc</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Gaspillage réseau -->
    <div v-if="showWasteStats" class="modal-overlay" @click="showWasteStats = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showWasteStats = false">✕</button>
        <h2 class="modal-title">♻️ Gaspillage réseau</h2>

        <p v-if="wasteLoading" class="feedback-empty">Chargement…</p>
        <p v-else-if="wasteError" class="feedback-empty">{{ wasteError }}</p>
        <template v-else>
          <div class="stats-row">
            <div class="stat-card stat-orange">
              <div class="stat-icon">🗑️</div>
              <div class="stat-value">{{ formatKg(networkWaste.totalKg) }}</div>
              <div class="stat-label">
                Sur la période ({{ networkWaste.canteenCount }} école{{ networkWaste.canteenCount > 1 ? "s" : "" }})
              </div>
            </div>
            <div
              v-if="wasteVariation !== null"
              class="stat-card"
              :class="wasteVariation <= 0 ? 'stat-green' : 'stat-orange'"
            >
              <div class="stat-icon">{{ wasteVariation <= 0 ? "📉" : "📈" }}</div>
              <div class="stat-value">{{ wasteVariation > 0 ? "+" : "" }}{{ wasteVariation }}%</div>
              <div class="stat-label">vs période précédente</div>
            </div>
          </div>

          <div class="detail-section">
            <h3 class="section-title">Par école</h3>
            <p v-if="!networkWaste.perCanteen.length" class="feedback-empty">
              Aucune pesée sur la période.
            </p>
            <div v-else class="school-waste-list">
              <div v-for="school in networkWaste.perCanteen" :key="school.canteenId" class="school-waste-item">
                <span class="school-name">{{ school.canteenName }}</span>
                <span class="waste-amount">{{ formatKg(school.totalKg) }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardCard from "@/components/DashboardCard.vue"

export default {
  name: "CookDashboard",
  components: { DashboardCard },
  data() {
    return {
      showTopRecipes: false,
      showToImprove: false,
      showSchoolFeedback: false,
      showPlanning: false,
      showWasteStats: false,
      // Bloc « Retours écoles » (décision B) : messages de catégorie KITCHEN adressés à la cuisine,
      // consolidés sur le périmètre du cuisinier par l'endpoint serveur.
      kitchenMessages: [],
      kitchenLoading: false,
      kitchenError: null,
      // Bloc « Gaspillage réseau » (§3.1) : agrégat serveur sur le périmètre du cuisinier.
      networkWaste: { totalKg: 0, canteenCount: 0, perCanteen: [], comparison: null },
      wasteLoading: false,
      wasteError: null,
      // Blocs « Top recettes » / « À améliorer » (§3.2, décision A) : classement des recettes nommées
      // du périmètre, agrégé côté serveur. Les notes par catégorie sont rattachées au plat du menu.
      recipePerformance: { top: [], toImprove: [] },
      recipeLoading: false,
      recipeError: null,
      // Bloc « Planification » (§2.4) : menus à venir sur le périmètre réseau, regroupés par date
      // puis par cantine d'origine. Aucune donnée fabriquée : seules les recettes importées.
      menuPlanning: { days: [] },
      planningLoading: false,
      planningError: null,
    }
  },
  computed: {
    wasteVariation() {
      const comparison = this.networkWaste.comparison
      return comparison && comparison.variationPct !== null ? comparison.variationPct : null
    },
    wasteBadge() {
      if (this.wasteVariation === null) return this.formatKg(this.networkWaste.totalKg)
      return `${this.wasteVariation > 0 ? "+" : ""}${this.wasteVariation}%`
    },
    topBadge() {
      const best = this.recipePerformance.top[0]
      return best ? this.formatRating(best.averageRating) : "—"
    },
    planningBadge() {
      const count = this.menuPlanning.days.length
      return count ? `${count} j` : "—"
    },
  },
  mounted() {
    this.fetchKitchenMessages()
    this.fetchNetworkWaste()
    this.fetchRecipePerformance()
    this.fetchMenuPlanning()
  },
  methods: {
    async fetchKitchenMessages() {
      this.kitchenLoading = true
      this.kitchenError = null
      try {
        const response = await fetch("/api/v1/kitchenCommunications/", { credentials: "same-origin" })
        if (response.ok) {
          const data = await response.json()
          this.kitchenMessages = Array.isArray(data) ? data : data.results || []
        } else {
          this.kitchenError = `Erreur ${response.status} lors du chargement des retours.`
        }
      } catch {
        this.kitchenError = "Impossible de charger les retours des écoles."
      } finally {
        this.kitchenLoading = false
      }
    },
    async fetchNetworkWaste() {
      this.wasteLoading = true
      this.wasteError = null
      try {
        const response = await fetch("/api/v1/networkWaste/", { credentials: "same-origin" })
        if (response.ok) {
          this.networkWaste = await response.json()
        } else {
          this.wasteError = `Erreur ${response.status} lors du chargement du gaspillage réseau.`
        }
      } catch {
        this.wasteError = "Impossible de charger le gaspillage réseau."
      } finally {
        this.wasteLoading = false
      }
    },
    formatKg(kg) {
      const value = Number(kg || 0)
      return `${value.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} kg`
    },
    formatMealDate(isoDate) {
      if (!isoDate) return ""
      return new Date(isoDate).toLocaleDateString("fr-FR", { day: "2-digit", month: "long" })
    },
    formatFullDate(isoDate) {
      if (!isoDate) return ""
      const formatted = new Date(isoDate).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    },
    async fetchMenuPlanning() {
      this.planningLoading = true
      this.planningError = null
      try {
        const response = await fetch("/api/v1/menuPlanning/", { credentials: "same-origin" })
        if (response.ok) {
          const data = await response.json()
          this.menuPlanning = { days: data.days || [] }
        } else {
          this.planningError = `Erreur ${response.status} lors du chargement de la planification.`
        }
      } catch {
        this.planningError = "Impossible de charger la planification des menus."
      } finally {
        this.planningLoading = false
      }
    },
    formatRating(rating) {
      const value = Number(rating || 0)
      return `${value.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}/5`
    },
    async fetchRecipePerformance() {
      this.recipeLoading = true
      this.recipeError = null
      try {
        const response = await fetch("/api/v1/recipePerformance/", { credentials: "same-origin" })
        if (response.ok) {
          const data = await response.json()
          this.recipePerformance = { top: data.top || [], toImprove: data.toImprove || [] }
        } else {
          this.recipeError = `Erreur ${response.status} lors du chargement du classement.`
        }
      } catch {
        this.recipeError = "Impossible de charger le classement des recettes."
      } finally {
        this.recipeLoading = false
      }
    },
  },
}
</script>

<style scoped>
.cook-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
}

/* Ranking */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.ranking-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 12px;
}

.ranking-number {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #ef4444;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
}

.ranking-details {
  flex: 1;
}

.ranking-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}

.ranking-score {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.score-emoji {
  font-size: 1.25rem;
}

.score-value {
  font-weight: 700;
  color: #22c55e;
}

.score-meta {
  font-size: 0.8rem;
  color: #6b7280;
}

.btn-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #eff6ff;
  color: #2563eb;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
}

/* Improve */
.improve-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.improve-card {
  background: #fff7ed;
  padding: 1.25rem;
  border-radius: 12px;
  border-left: 4px solid #f97316;
}

.improve-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.improve-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.improve-score {
  font-weight: 700;
  color: #f97316;
}

.improve-feedback {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.feedback-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}

.feedback-text {
  font-size: 0.875rem;
  color: #1a1a1a;
}

.feedback-count {
  font-size: 0.75rem;
  color: #6b7280;
}

.btn-secondary-small {
  background: white;
  color: #2563eb;
  border: 2px solid #e5e7eb;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

/* Feedback écoles */
.feedback-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.feedback-card {
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.feedback-positive {
  background: #f0fdf4;
  border-color: #22c55e;
}

.feedback-negative {
  background: #fef2f2;
  border-color: #ef4444;
}

.feedback-neutral {
  background: #eff6ff;
  border-color: #2563eb;
}

.feedback-school {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.feedback-time {
  font-size: 0.75rem;
  color: #6b7280;
}

.feedback-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}

.feedback-message {
  font-size: 0.875rem;
  color: #1a1a1a;
  margin: 0;
}

.feedback-empty {
  font-size: 0.875rem;
  color: #6b7280;
  text-align: center;
  padding: 1.5rem 0;
}

/* Planning */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.planning-card {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.planning-date {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2563eb;
  margin-bottom: 0.5rem;
}

.planning-canteen {
  margin-top: 0.75rem;
}

.planning-canteen:first-child {
  margin-top: 0;
}

.planning-canteen-name {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.35rem;
}

.menu-pork {
  font-size: 0.8125rem;
  color: #b45309;
  white-space: nowrap;
}

.trend-text {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
}

/* Stats */
.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 2px solid;
}

.stat-card.stat-orange {
  border-color: #fdba74;
  background: #fff7ed;
}
.stat-card.stat-green {
  border-color: #86efac;
  background: #f0fdf4;
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.school-waste-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.school-waste-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.school-name {
  font-size: 0.875rem;
  color: #1a1a1a;
}

.waste-amount {
  font-size: 0.875rem;
  font-weight: 700;
  color: #f97316;
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}
</style>
